import {useCallback, useEffect, useState} from 'react';

export type PermissionKind = 'microphone' | 'notifications';
export type PermissionValue = 'granted' | 'denied' | 'prompt' | 'unsupported';

async function readPermission(kind: PermissionKind): Promise<PermissionValue> {
    if (kind === 'notifications') {
        if (typeof Notification === 'undefined') return 'unsupported';
        return Notification.permission === 'default' ? 'prompt' : Notification.permission;
    }
    if (!navigator.mediaDevices?.getUserMedia) return 'unsupported';
    try {
        const status = await navigator.permissions.query({name: 'microphone' as PermissionName});
        return status.state;
    } catch {
        // Some browsers cannot query the microphone; it is asked for on first use.
        return 'prompt';
    }
}

async function requestPermission(kind: PermissionKind): Promise<void> {
    if (kind === 'notifications') {
        await Notification.requestPermission();
        return;
    }
    const stream = await navigator.mediaDevices.getUserMedia({audio: true});
    stream.getTracks().forEach((track) => track.stop());
}

/** Current browser permission for calls, re-read when it changes; `request` asks the browser. */
export function useBrowserPermission(kind: PermissionKind) {
    const [state, setState] = useState<PermissionValue | undefined>(undefined);

    const refresh = useCallback(() => {
        readPermission(kind).then(setState);
    }, [kind]);

    useEffect(() => {
        refresh();
        let status: PermissionStatus | undefined;
        const name = (kind === 'notifications' ? 'notifications' : 'microphone') as PermissionName;
        navigator.permissions?.query({name}).then((s) => {
            status = s;
            s.addEventListener('change', refresh);
        }).catch(() => undefined);
        window.addEventListener('focus', refresh);
        return () => {
            status?.removeEventListener('change', refresh);
            window.removeEventListener('focus', refresh);
        };
    }, [kind, refresh]);

    const request = useCallback(async () => {
        try {
            await requestPermission(kind);
        } finally {
            refresh();
        }
    }, [kind, refresh]);

    return {state, request};
}

export const allAllowed = (...states: (PermissionValue | undefined)[]) =>
    states.every((s) => s === 'granted' || s === 'unsupported');
